import { Item, Order_Output as Order } from "@/api";
import { formatDate, formatNumber } from "@/utils/format";

interface EchoItemOrderDataTableProps {
  items: Item[];
  orders: Order[];
  highlightedOrderIds: string[];
  taskLinkedOrderIds: Set<string>;
  // 各注文IDに対して、productionTask群が割り当てた良品数の合計
  allocatedQuantityByOrderId: Record<string, number>;
  // 各注文IDに対して、既存在庫から充当された数量の合計
  inventoryAllocatedQuantityByOrderId: Record<string, number>;
  // 納期遅れが発生している注文IDに対して、OrderAllocation.reasonの文言を保持する
  lateOrderReasonByOrderId: Record<string, string>;
  // 最適化対象期間。指定されている場合、期間外(due_date < horizonStart, > horizonEnd)の注文は除外する
  horizonStart?: string;
  horizonEnd?: string;
}

const EchoItemOrderDataTable = ({
  items,
  orders,
  highlightedOrderIds,
  taskLinkedOrderIds,
  allocatedQuantityByOrderId,
  inventoryAllocatedQuantityByOrderId,
  lateOrderReasonByOrderId,
  horizonStart,
  horizonEnd,
}: EchoItemOrderDataTableProps) => {
  const selectedIds = new Set(items.map((i) => i.品目ID));
  const highlightedSet = new Set(highlightedOrderIds);

  const rows = orders
    .filter((o) => selectedIds.has(o.sku_id))
    .filter((o) => {
      if (horizonStart && o.due_date < horizonStart) return false;
      if (horizonEnd && o.due_date > horizonEnd) return false;
      return true;
    })
    .sort((a, b) => a.due_date.localeCompare(b.due_date));

  if (rows.length === 0) {
    return <div className="text-sm text-gray-500">選択中の品目に対応する注文がありません</div>;
  }

  const totalQty = rows.reduce((sum, o) => sum + (o.quantity ?? 0), 0);
  const totalAllocated = rows.reduce((sum, o) => sum + (allocatedQuantityByOrderId[o.order_id] ?? 0), 0);
  const totalInventoryAllocated = rows.reduce(
    (sum, o) => sum + (inventoryAllocatedQuantityByOrderId[o.order_id] ?? 0),
    0,
  );
  const lateOrderCount = rows.reduce(
    (count, o) => count + (lateOrderReasonByOrderId[o.order_id] ? 1 : 0),
    0,
  );
  const uniqueDest = new Set(rows.map((o) => o.destination)).size;
  const horizonLabel = horizonStart && horizonEnd ? ` / 対象期間: ${formatDate(horizonStart)}〜${formatDate(horizonEnd)}` : "";

  return (
    <div className="flex flex-column gap-3">
      <div className="text-sm text-gray-700">
        注文件数: {rows.length} / 注文数量合計: {totalQty} / 処理済数量合計: {totalAllocated} / 在庫充当合計: {totalInventoryAllocated} / 納入拠点数: {uniqueDest}
        {lateOrderCount > 0 && (
          <span className="ml-2 text-red-600 font-medium">/ 納期遅れ: {lateOrderCount}件</span>
        )}
        {horizonLabel}
      </div>
      <div className="overflow-auto">
        <table className="w-full table-auto text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="p-2 text-left">品目ID</th>
              <th className="p-2 text-left">品名</th>
              <th className="p-2 text-left">納入拠点</th>
              <th className="p-2 text-left">種別</th>
              <th className="p-2 text-right">数量</th>
              <th className="p-2 text-right">処理済</th>
              <th className="p-2 text-right">在庫充当</th>
              <th className="p-2 text-right">充足率</th>
              <th className="p-2 text-left">納期</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o, idx) => {
              const item = items.find((i) => i.品目ID === o.sku_id);
              const typeLabel = o.order_type;
              const isHighlighted = highlightedSet.has(o.order_id);
              const isTaskLinked = taskLinkedOrderIds.has(o.order_id);
              const allocated = allocatedQuantityByOrderId[o.order_id] ?? 0;
              const inventoryAllocated = inventoryAllocatedQuantityByOrderId[o.order_id] ?? 0;
              const fulfilled = allocated + inventoryAllocated;
              const ratio = o.quantity > 0 ? fulfilled / o.quantity : 0;
              const ratioLabel = `${(ratio * 100).toFixed(0)}%`;
              const shortage = fulfilled < o.quantity;
              const isInventoryAllocated = inventoryAllocated > 0;
              const lateReason = lateOrderReasonByOrderId[o.order_id];
              const isLate = Boolean(lateReason);
              const rowClassName = isHighlighted
                ? "row-highlight"
                : isLate
                  ? "bg-red-50"
                  : isInventoryAllocated
                    ? "bg-blue-50"
                    : isTaskLinked
                      ? "bg-yellow-50"
                      : "odd:bg-white even:bg-gray-50";
              return (
                <tr key={`${o.order_id}#${idx}`} className={rowClassName}>
                  <td className="p-2 whitespace-nowrap">{o.sku_id}</td>
                  <td className="p-2">{item?.製造分類 ?? ""}</td>
                  <td className="p-2 whitespace-nowrap">{o.destination}</td>
                  <td className="p-2 whitespace-nowrap">{typeLabel}</td>
                  <td className="p-2 text-right">{formatNumber(o.quantity)}</td>
                  <td className={`p-2 text-right ${shortage ? "text-red-600" : ""}`}>{formatNumber(allocated)}</td>
                  <td className={`p-2 text-right ${isInventoryAllocated ? "text-blue-700 font-medium" : "text-gray-400"}`}>
                    {isInventoryAllocated ? formatNumber(inventoryAllocated) : "-"}
                  </td>
                  <td className={`p-2 text-right ${shortage ? "text-red-600" : ""}`}>{ratioLabel}</td>
                  <td className={`p-2 whitespace-nowrap ${isLate ? "text-red-700 font-medium" : ""}`}>
                    {formatDate(o.due_date)}
                    {isLate && (
                      <span className="ml-2 text-xs text-red-700">⚠ {lateReason}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EchoItemOrderDataTable;
